import { ThemeKeys } from 'teacher-ui-client/utils/themes'

// Note the theme keys are the _values_ in this enum.
export enum TAB_TYPE_KEYS {
  shadow = 'shadow-sm',
  underline = 'underline',
  border = 'border',
  borderUnderline = 'borderUnderline',
  borderClassic = 'borderClassic',
}

export const TAB_TYPE_CLASSES = {
  [TAB_TYPE_KEYS.shadow]: 'tab-type-shadow block',
  [TAB_TYPE_KEYS.underline]: 'tab-type-underline flex',
  [TAB_TYPE_KEYS.border]: 'tab-type-border block',
  [TAB_TYPE_KEYS.borderUnderline]: 'tab-type-border-underline flex gap-2',
  [TAB_TYPE_KEYS.borderClassic]: 'tab-type-border-classic flex',
}

/**
 * Provide a tab type key, and get back a tab class name. This can be applied to an element to make color-based UI
 * decisions in CSS.
 *
 * tabTypeKeyToTabClass("shadow-sm") -> "tab-type-shadow"
 * tabTypeKeyToTabClass("underline") -> "tab-type-underline"
 */
export function getTabTypeGroupClasses(tabTypeKey: TAB_TYPE_KEYS): string {
  return TAB_TYPE_CLASSES[tabTypeKey]
}

/**
 * Type guard function to narrow type to tabTypeKey
 */
export function isTabTypeKey(maybeTabTypeKey: unknown): maybeTabTypeKey is TAB_TYPE_KEYS {
  return Object.values(TAB_TYPE_KEYS).includes(maybeTabTypeKey as TAB_TYPE_KEYS)
}

/**
 * Classnames to use for a tab type across all themes
 */
export function getTabTypeClasses(tabTypeKey?: TAB_TYPE_KEYS): string {
  switch (tabTypeKey) {
    case TAB_TYPE_KEYS.shadow:
      return 'inline-block p-3 bg-white shadow-[0_0_12px] [clip-path:inset(-12px_-12px_0_-12px)] shadow-dusty-black-100 cursor-pointer text-dusty-black-300 rounded-t-lg font-bold mr-1 focus:text-dusty-black-300 hover:text-dusty-black-300 active-link:relative active-link:z-[1] print:hidden print:active-link:block print:bg-transparent print:text-xl print:pl-0 print:shadow-none'
    case TAB_TYPE_KEYS.underline:
      return 'inline-block grow mx-4 p-2 bg-white border-b-2 border-dusty-black-100 cursor-pointer text-dusty-black-300 text-center font-semibold print:hidden print:active-link:block print:max-w-[50%] print:mx-auto print:bg-transparent print:border-black!'
    case TAB_TYPE_KEYS.border:
      return 'inline-block px-3 py-2 bg-white border-t border-r border-l border-dusty-black-100 cursor-pointer text-dusty-black-300 rounded-t-lg font-bold mr-1 focus:text-dusty-black-300 hover:text-dusty-black-300 active-link:relative active-link:z-[1] print:hidden print:active-link:block print:bg-transparent print:text-xl print:pl-0'
    case TAB_TYPE_KEYS.borderUnderline:
      return 'inline-block basis-36 bg-white cursor-pointer rounded-t-lg p-3 border border-dusty-black-100 text-center font-bold text-dusty-black-300 focus:text-dusty-black-300 hover:text-dusty-black-300 active-link:border-b-transparent active-link:underline print:hidden print:active-link:block print:text-xl'
    case TAB_TYPE_KEYS.borderClassic:
      return 'inline-block basis-56 bg-white cursor-pointer p-3 -mr-px border-t border-r border-l border-dusty-black-100 rounded-t-lg text-dusty-black-300 focus:text-dusty-black-300 hover:text-dusty-black-300 text-center print:hidden print:active-link:block'
    default:
      return 'print:hidden print:active-link:block'
  }
}

/**
 * Classnames that are specific to both the tab type and theme
 */
export function getTabThemeClasses(tabTypeKey?: TAB_TYPE_KEYS, colorThemeKeys?: ThemeKeys): string {
  switch (tabTypeKey) {
    case TAB_TYPE_KEYS.shadow:
      switch (colorThemeKeys) {
        case ThemeKeys.blue:
          return 'focus:bg-oceany-blue-100 hover:bg-oceany-blue-100 active-link:bg-oceany-blue-100'
        case ThemeKeys.green:
          return 'focus:bg-ms-green-100 hover:bg-ms-green-100 active-link:bg-ms-green-100'
        case ThemeKeys.orange:
          return 'focus:bg-juicy-orange-100 hover:bg-juicy-orange-100 active-link:bg-juicy-orange-100'
        case ThemeKeys.purple:
          return 'focus:bg-purply-pink-100 hover:bg-purply-pink-100 active-link:bg-purply-pink-100'
        case ThemeKeys.darkBlue:
          return 'focus:bg-wl-blue-100 hover:bg-wl-blue-100 active-link:bg-wl-blue-100'
        default:
          return 'focus:bg-dusty-black-200 hover:bg-dusty-black-200 active-link:bg-dusty-black-200'
      }
    case TAB_TYPE_KEYS.underline:
      switch (colorThemeKeys) {
        case ThemeKeys.blue:
          return 'focus:text-oceany-blue-350 hover:text-oceany-blue-350 active-link:text-oceany-blue-350 focus:border-oceany-blue-350 hover:border-oceany-blue-350 active-link:border-oceany-blue-350'
        case ThemeKeys.green:
          return 'focus:text-ms-green-350 hover:text-ms-green-350 active-link:text-ms-green-350 focus:border-ms-green-350 hover:border-ms-green-350 active-link:border-ms-green-350'
        case ThemeKeys.orange:
          return 'focus:text-juicy-orange-350 hover:text-juicy-orange-350 active-link:text-juicy-orange-350 focus:border-juicy-orange-350 hover:border-juicy-orange-350 active-link:border-juicy-orange-350'
        case ThemeKeys.purple:
          return 'focus:text-purply-pink-350 hover:text-purply-pink-350 active-link:text-purply-pink-350 focus:border-purply-pink-350 hover:border-purply-pink-350 active-link:border-purply-pink-350'
        case ThemeKeys.darkBlue:
          return 'focus:text-wl-blue-300 hover:text-wl-blue-300 active-link:text-wl-blue-300 focus:border-wl-blue-300 hover:border-wl-blue-300 active-link:border-wl-blue-300'
        default:
          return 'focus:text-black hover:text-black active-link:text-black focus:border-black hover:border-black active-link:border-black'
      }
    case TAB_TYPE_KEYS.border:
      switch (colorThemeKeys) {
        case ThemeKeys.blue:
          return 'focus:bg-oceany-blue-100 hover:bg-oceany-blue-100 active-link:bg-oceany-blue-100'
        case ThemeKeys.green:
          return 'focus:bg-ms-green-100 hover:bg-ms-green-100 active-link:bg-ms-green-100'
        case ThemeKeys.orange:
          return 'focus:bg-juicy-orange-100 hover:bg-juicy-orange-100 active-link:bg-juicy-orange-100'
        case ThemeKeys.purple:
          return 'focus:bg-purply-pink-100 hover:bg-purply-pink-100 active-link:bg-purply-pink-100'
        default:
          return 'focus:bg-dusty-black-200 hover:bg-dusty-black-200 active-link:bg-dusty-black-200'
      }
    case TAB_TYPE_KEYS.borderUnderline:
      switch (colorThemeKeys) {
        case ThemeKeys.blue:
          return 'focus:text-oceany-blue-350 hover:text-oceany-blue-350 active-link:text-oceany-blue-350'
        case ThemeKeys.green:
          return 'focus:text-ms-green-350 hover:text-ms-green-350 active-link:text-ms-green-350'
        case ThemeKeys.orange:
          return 'focus:text-juicy-orange-350 hover:text-juicy-orange-350 active-link:text-juicy-orange-350'
        case ThemeKeys.purple:
          return 'focus:text-purply-pink-350 hover:text-purply-pink-350 active-link:text-purply-pink-350'
        default:
          return 'focus:text-black hover:text-black active-link:text-black'
      }
    case TAB_TYPE_KEYS.borderClassic:
      switch (colorThemeKeys) {
        case ThemeKeys.blue:
          return 'focus:bg-oceany-blue-150 hover:bg-oceany-blue-150 active-link:bg-oceany-blue-150'
        case ThemeKeys.green:
          return 'focus:bg-ms-green-150 hover:bg-ms-green-150 active-link:bg-ms-green-150'
        case ThemeKeys.darkBlue:
          return 'focus:bg-wl-blue-100 hover:bg-wl-blue-100 active-link:bg-wl-blue-100'
        case ThemeKeys.orange:
          return 'focus:bg-juicy-orange-100 hover:bg-juicy-orange-100 active-link:bg-juicy-orange-100'
        case ThemeKeys.purple:
          return 'focus:bg-purply-pink-100 hover:bg-purply-pink-100 active-link:bg-purply-pink-100'
        default:
          return 'focus:bg-dusty-black-200 hover:bg-dusty-black-200 active-link:bg-dusty-black-200'
      }
    default:
      return ''
  }
}
